import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle, Download, FileText, AlertTriangle, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { AssessmentGate } from '@/components/AssessmentGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuestionnairePage = () => {
  const [config, setConfig] = useState(null);
  const [sectionIndex, setSectionIndex] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const { t, pick, lang } = useLang();
  const { user, token, loading: authLoading } = useAuth();

  useEffect(() => {
    axios.get(`${API}/assessment/config`).then(r => setConfig(r.data)).catch(() => toast.error(t('q.load_fail')));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) setAnswers(prev => ({ full_name: user.name || '', phone: user.phone || '', ...prev }));
  }, [user]);

  const currentSection = config?.sections?.[sectionIndex];

  // Filter visible questions based on conditional logic
  const visibleQuestions = useMemo(() => {
    if (!currentSection) return [];
    return currentSection.questions.filter(q => {
      if (!q.conditional_parent_id) return true;
      const parentVal = answers[q.conditional_parent_id];
      if (Array.isArray(parentVal)) return parentVal.includes(q.conditional_trigger_value);
      return String(parentVal) === String(q.conditional_trigger_value);
    });
  }, [currentSection, answers]);

  const totalSections = config?.sections?.length || 0;
  const progress = totalSections > 0 ? ((sectionIndex + 1) / (totalSections + 1)) * 100 : 0;

  const setAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
    setErrors(prev => { const n = { ...prev }; delete n[qId]; return n; });
  };

  const toggleCheckbox = (qId, value) => {
    setAnswers(prev => {
      const current = prev[qId] || [];
      const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, [qId]: updated };
    });
    setErrors(prev => { const n = { ...prev }; delete n[qId]; return n; });
  };

  const validateSection = () => {
    const newErrors = {};
    for (const q of visibleQuestions) {
      if (!q.required) continue;
      const val = answers[q.id];
      if (val === undefined || val === null || val === '') {
        newErrors[q.id] = true;
      }
      if (q.type === 'checkbox' && (!val || val.length === 0)) {
        newErrors[q.id] = true;
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(t('common.fill_required'));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (sectionIndex >= 0 && !validateSection()) return;
    if (sectionIndex < totalSections - 1) {
      setSectionIndex(sectionIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (sectionIndex > -1) {
      setSectionIndex(sectionIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!validateSection()) return;
    if (!answers.terms_accepted) {
      toast.error(t('q.need_terms'));
      return;
    }
    if (!answers.acknowledgment) {
      toast.error(t('q.need_ack'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessment/submit`, { answers }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
      toast.success(t('q.submitted_toast'));
    } catch {
      toast.error(t('q.submit_fail'));
    }
    setSubmitting(false);
  };

  const downloadPdf = () => {
    if (result?.id) {
      window.open(`${API}/assessment/${result.id}/pdf?token=${token}&lang=${lang}`, '_blank');
    }
  };

  if (!authLoading && !user) return <AssessmentGate />;
  if (user?.role === 'admin') return <AssessmentGate isAdmin />;

  // Result screen
  if (result) {
    const isSigned = result.coach_signed;
    return (
      <div key="assessment-result-page" className="pt-20 min-h-screen flex flex-col justify-center" data-testid="assessment-result">
        <div className="max-w-2xl mx-auto px-6 py-16 w-full min-w-0 text-center">
          <motion.div key="assessment-result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <CheckCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="text-4xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-4">
              {t('q.done_title')}
            </h1>
            <p className="text-[#A3A19C] mb-2">{t('q.done_sub')}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#121215] border border-[rgba(255,255,255,0.08)] mb-8">
              <div className={`w-2 h-2 rounded-full ${isSigned ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
              <span className="text-sm text-[#A3A19C]">
                {isSigned ? t('q.signed_badge') : t('q.pending_badge')}
              </span>
            </div>
            <p className="text-sm text-[#8C6C73] mb-10">
              {isSigned ? t('q.signed_text') : t('q.pending_text')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {isSigned ? (
                <button onClick={downloadPdf} className="btn-primary inline-flex items-center gap-2" data-testid="download-pdf-btn">
                  <Download className="w-4 h-4" strokeWidth={1.5} /> {t('q.download')}
                </button>
              ) : (
                <button disabled className="btn-primary opacity-50 cursor-not-allowed inline-flex items-center gap-2" data-testid="download-pdf-btn-disabled">
                  <Download className="w-4 h-4" strokeWidth={1.5} /> {t('q.awaiting')}
                </button>
              )}
              <a href="https://api.whatsapp.com/send/?phone=97333216371&text=I%20just%20completed%20my%20fitness%20assessment!" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2">
                {t('q.contact_coach')}
              </a>
            </div>
            <p className="text-xs text-[#A3A19C] mt-8">{t('q.reference')}: <span className="ltr-iso">{result.id?.slice(0, 8)}</span></p>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><p className="text-[#A3A19C]">{t('q.loading')}</p></div>;
  }

  // Intro screen
  if (sectionIndex === -1) {
    return (
      <div className="pt-20 min-h-screen flex items-center" data-testid="assessment-intro">
        <div className="max-w-2xl mx-auto px-6 py-24 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="overline mb-4">{t('q.overline')}</p>
            <h1 data-testid="assessment-title" className="text-4xl lg:text-5xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-8">
              {pick(config, 'title')}
            </h1>
            <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-8 mb-10">
              <p data-testid="assessment-description" className="text-[#E2B4BD] text-lg leading-relaxed font-['Cormorant_Garamond']">
                {pick(config, 'description')}
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm text-[#A3A19C] mb-10">
              <FileText className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              <span>{t('q.meta', { n: totalSections })}</span>
            </div>
            <button onClick={() => setSectionIndex(0)} className="btn-primary inline-flex items-center gap-2 text-base" data-testid="start-assessment-btn">
              {t('q.start')} <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const isLastSection = sectionIndex === totalSections - 1;

  return (
    <div className="pt-20 min-h-screen" data-testid="assessment-page">
      {/* Progress */}
      <div className="fixed top-20 left-0 right-0 z-40 h-1 bg-[#121215]">
        <motion.div className="h-full bg-[#D4AF37]" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} data-testid="assessment-progress" />
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
        <AnimatePresence mode="wait">
          <motion.div key={sectionIndex} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}>
            {/* Section Header */}
            <div className="mb-10">
              <p className="overline mb-2">{t('q.section_of', { i: sectionIndex + 1, n: totalSections })}</p>
              <h2 data-testid="section-title" className="text-3xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{pick(currentSection, 'title')}</h2>
            </div>

            {/* Questions */}
            <div className="space-y-8">
              {visibleQuestions.map((q) => (
                <QuestionRenderer
                  key={q.id}
                  question={q}
                  value={answers[q.id]}
                  onChange={(val) => setAnswer(q.id, val)}
                  onToggle={(val) => toggleCheckbox(q.id, val)}
                  hasError={!!errors[q.id]}
                />
              ))}
            </div>

            {/* Terms & Conditions + Acknowledgment on last section */}
            {isLastSection && (
              <>
                {/* Terms & Conditions */}
                <div className="mt-12 bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-8" data-testid="terms-section">
                  <div className="flex items-start gap-3 mb-4">
                    <FileText className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">
                      {t('q.terms_title')}
                    </h3>
                  </div>
                  <p className="text-sm text-[#A3A19C] leading-relaxed mb-5">
                    {t('q.terms_text')}
                  </p>
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#E5C07B] transition-colors duration-300 mb-6 border border-[rgba(212,175,55,0.2)] rounded-full px-5 py-2.5"
                    data-testid="terms-link"
                  >
                    <FileText className="w-4 h-4" strokeWidth={1.5} />
                    {t('q.read_terms')}
                    <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </a>
                  <div className="mt-2">
                    <label className="flex items-center gap-3 cursor-pointer group" data-testid="terms-checkbox">
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors duration-300 ${
                        answers.terms_accepted
                          ? 'bg-[#D4AF37] border-[#D4AF37]'
                          : 'border-[rgba(255,255,255,0.2)] group-hover:border-[#D4AF37]'
                      }`} onClick={() => setAnswer('terms_accepted', !answers.terms_accepted)}>
                        {answers.terms_accepted && <CheckCircle className="w-4 h-4 text-[#08080A]" strokeWidth={2} />}
                      </div>
                      <span className="text-sm text-[#F5F2EB] font-medium">{t('q.agree_terms')}</span>
                    </label>
                  </div>
                </div>

                {/* Responsibility Acknowledgment */}
                <div className="mt-4 bg-[#121215] border border-[rgba(212,175,55,0.2)] rounded-lg p-8" data-testid="acknowledgment-section">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">
                      {t('q.ack_title')}
                    </h3>
                  </div>
                  <p className="text-sm text-[#A3A19C] leading-relaxed mb-6" data-testid="acknowledgment-text">
                    {pick(config, 'acknowledgment_text')}
                  </p>
                  <label className="flex items-center gap-3 cursor-pointer group" data-testid="acknowledgment-checkbox">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors duration-300 ${
                      answers.acknowledgment
                        ? 'bg-[#D4AF37] border-[#D4AF37]'
                        : 'border-[rgba(255,255,255,0.2)] group-hover:border-[#D4AF37]'
                    }`} onClick={() => setAnswer('acknowledgment', !answers.acknowledgment)}>
                      {answers.acknowledgment && <CheckCircle className="w-4 h-4 text-[#08080A]" strokeWidth={2} />}
                    </div>
                    <span className="text-sm text-[#F5F2EB] font-medium">{t('q.ack_accept')}</span>
                  </label>
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12 pt-8 border-t border-[rgba(255,255,255,0.06)]">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm font-medium text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300"
                data-testid="assessment-back"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t('q.back')}
              </button>

              {isLastSection ? (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`btn-primary inline-flex items-center gap-2 ${submitting ? 'opacity-50' : ''}`}
                  data-testid="assessment-submit"
                >
                  {submitting ? t('q.submitting') : t('q.submit')} <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                </button>
              ) : (
                <button onClick={handleNext} className="btn-primary inline-flex items-center gap-2" data-testid="assessment-next">
                  {t('q.next')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// ============== QUESTION RENDERER ==============
const QuestionRenderer = ({ question, value, onChange, onToggle, hasError }) => {
  const q = question;
  const { t, pick } = useLang();
  const borderClass = hasError ? 'border-red-500/50' : 'border-[rgba(255,255,255,0.1)]';

  return (
    <div className="space-y-3" data-testid={`question-${q.id}`}>
      <label className="block">
        <span className="text-sm font-semibold text-[#F5F2EB]">{pick(q, 'text')}</span>
        {q.required && <span className="text-[#D4AF37] ms-1">*</span>}
      </label>
      {(q.hint || q.hint_ar) && <p className="text-xs text-[#8C6C73]">{pick(q, 'hint')}</p>}

      {/* TEXT INPUT */}
      {(q.type === 'text') && (
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          data-testid={`input-${q.id}`}
          className={`w-full bg-[#121215] border ${borderClass} rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300`}
          placeholder={pick(q, 'hint')}
        />
      )}

      {/* NUMBER INPUT */}
      {q.type === 'number' && (
        <input
          type="number"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          data-testid={`input-${q.id}`}
          className={`w-full bg-[#121215] border ${borderClass} rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300`}
          placeholder={pick(q, 'hint')}
        />
      )}

      {/* TEXTAREA */}
      {q.type === 'textarea' && (
        <textarea
          rows={3}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          data-testid={`input-${q.id}`}
          className={`w-full bg-[#121215] border ${borderClass} rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300 resize-none`}
          placeholder={pick(q, 'hint')}
        />
      )}

      {/* DATE */}
      {q.type === 'date' && (
        <input
          type="date"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          data-testid={`input-${q.id}`}
          className={`w-full bg-[#121215] border ${borderClass} rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300 [color-scheme:dark]`}
        />
      )}

      {/* RADIO */}
      {q.type === 'radio' && q.options && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {q.options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-testid={`option-${q.id}-${opt.value}`}
              className={`questionnaire-card flex items-center gap-3 text-start py-3 px-4 ${value === opt.value ? 'selected' : ''} ${hasError && !value ? '!border-red-500/50' : ''}`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                value === opt.value ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-[rgba(255,255,255,0.2)]'
              }`}>
                {value === opt.value && <div className="w-2 h-2 rounded-full bg-[#08080A]" />}
              </div>
              <div>
                <span className="text-sm text-[#F5F2EB]">{pick(opt, 'label')}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* CHECKBOX (multi-select) */}
      {q.type === 'checkbox' && q.options && (
        <div className="space-y-2">
          {q.options.map(opt => {
            const checked = (value || []).includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                data-testid={`checkbox-${q.id}-${opt.value}`}
                className={`w-full questionnaire-card flex items-center gap-3 text-start py-3 px-4 ${checked ? 'selected' : ''} ${hasError && (!value || value.length === 0) ? '!border-red-500/50' : ''}`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                  checked ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-[rgba(255,255,255,0.2)]'
                }`}>
                  {checked && <CheckCircle className="w-3 h-3 text-[#08080A]" strokeWidth={2} />}
                </div>
                <div>
                  <span className="text-sm text-[#F5F2EB]">{pick(opt, 'label')}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* SCALE (1-10) */}
      {q.type === 'scale' && (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: (q.scale_max || 10) - (q.scale_min || 1) + 1 }, (_, i) => (q.scale_min || 1) + i).map(num => (
            <button
              key={num}
              type="button"
              onClick={() => onChange(num)}
              data-testid={`scale-${q.id}-${num}`}
              className={`w-11 h-11 rounded-lg border flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                value === num
                  ? 'bg-[#D4AF37] border-[#D4AF37] text-[#08080A]'
                  : `bg-[#121215] ${borderClass} text-[#A3A19C] hover:border-[rgba(212,175,55,0.3)] hover:text-[#F5F2EB]`
              }`}
            >
              {num}
            </button>
          ))}
        </div>
      )}

      {hasError && <p className="text-xs text-red-400">{t('common.required_field')}</p>}
    </div>
  );
};

export default QuestionnairePage;
